/* eslint-disable promise/catch-or-return */
import React, { Component } from 'react';

import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';

import { messages } from '@qality/common-messages';
import {
  Issue,
  LastExecution,
  TestCase,
  TestStepsView,
} from '@qality/common-model';
import { TestCaseFullScreenCloseCallbackData } from '@qality/common-model/src/TestCaseFullScreenData';
import {
  StorageOfUniqueDialogs,
  StorageOfUniqueServices,
  TestStepService,
} from '@qality/common-service';
import { QAlityIssueViewContext } from '@qality/common-state';

import EditableTitle from '../EditableTitle';
import FullscreenButton from '../molecules/FullscreenButton/FullscreenButton';
import ChangeView from './ChangeView';
import TestCaseBoxActions from './TestCaseBoxActions';
import TestCaseBoxTestType from './TestCaseBoxTestType';
import './_test-case-box-title.scss';

type Props = {
  saveTestCaseName: (name: string) => Promise<boolean | void> | void;
  deleteTestCase?: () => void;
  canEdit: boolean;
  onToggleVisibility?: (value: boolean) => void;
  name: string;
  addTestStep: () => Promise<boolean | void>;
  expanded: boolean;
  testCase: TestCase;
  issueKey: string;
  isTestStepView?: boolean;
  isHovered?: boolean;
  isIssueView?: boolean;
  onDropdownOpen?: boolean;
  isFullscreenMode?: boolean;
  viewProps?: {
    onViewChange: (value: TestStepsView) => void;
    view: TestStepsView;
  };
  syncDataFromFullScreen: (data: TestCaseFullScreenCloseCallbackData) => void;
  onDialogClose?: () => void;
  lastExecution: LastExecution;
};

type State = {
  name: string;
  expanded: boolean;
  isNameInvalid: boolean;
  isTestCycleDialogOpen: boolean;
  testCaseIssueKey: string;
  nameEdited: boolean;
  isEditing: boolean;
  isDropdownOpen: boolean;
  lastExecution: LastExecution;
  issueId: number;
};

export default class TestCaseBoxTitle extends Component<Props, State> {
  static defaultProps = {
    isFullscreenMode: false,
  };

  static contextType = QAlityIssueViewContext;

  oldName: string;
  editableTitleRef: React.RefObject<HTMLDivElement>;

  constructor(props: Props) {
    super(props);
    const issue = props.testCase.issue as Issue;
    this.state = {
      name: props.name,
      nameEdited: false,
      expanded: this.props.expanded,
      isNameInvalid: false,
      isTestCycleDialogOpen: false,
      testCaseIssueKey: issue.key ? issue.key : '',
      issueId: issue.id ? Number(issue.id) : null,
      isEditing: false,
      isDropdownOpen: false,
      lastExecution: null,
    };
    this.oldName = props.name;
    this.editableTitleRef = React.createRef();
  }

  componentDidUpdate(prevProps: Props): void {
    if (!this.state.nameEdited && prevProps.name !== this.props.name) {
      this.oldName = this.props.name;
      this.setState({ name: this.props.name });
    }
    if (prevProps.expanded !== this.props.expanded) {
      this.setState({ expanded: this.props.expanded });
    }
    StorageOfUniqueServices.get('ResizeService').resize();
    if (!(this.props.testCase.issue as Issue).key) {
      (this.props.testCase.issue as Promise<Issue>).then((issue) => {
        // eslint-disable-next-line promise/always-return
        if (this.state.testCaseIssueKey !== issue.key) {
          this.setState({
            testCaseIssueKey: issue.key,
            issueId: Number(issue.id),
          });
        }
      });
    }
  }

  toggleVisibility = (): void => {
    const value = !this.state.expanded;
    this.setState({
      expanded: value,
    });
    if (this.props.onToggleVisibility) {
      this.props.onToggleVisibility(value);
    }
  };

  displayTestCaseBoxActions = (): boolean => {
    return (
      (this.props.isIssueView && this.props.isHovered) ||
      !this.props.isIssueView ||
      this.props.expanded ||
      this.state.isDropdownOpen ||
      this.state.isEditing
    );
  };

  onDropdownChange = (): void => {
    this.setState({ isDropdownOpen: !this.state.isDropdownOpen });
  };

  onIsEditingChange = (isEditing: boolean): void => {
    this.setState({ isEditing });
  };

  openTestCaseInFullScreen = async (): Promise<void> => {
    const currentIssue = await this.props.testCase.issue;

    const { testCase, lastExecution } = this.props;
    testCase.issue = currentIssue;

    testCase.testSteps.testSteps =
      await TestStepService.convertOriginPromisesToObjects(testCase);

    StorageOfUniqueDialogs.get(
      'TestCaseFullScreenDialog'
    ).openTestCaseFullScreenDialog({
      qalityIssueViewContextInitProps: {
        currentIssue,
        currentUser: this.context.currentUser,
        currentUserPermissions: this.context.currentUserPermissions,
      },
      qalityIssueViewInitProps: {
        testCase,
        lastExecution,
      },
      onFullScreenClose: this.onFullScreenClose,
    });
  };

  onFullScreenClose = (data: TestCaseFullScreenCloseCallbackData): void => {
    if (!data) {
      return;
    }

    // Passing class objects with AP.dialog.create() drops the class identity
    // of these objects. Therefore to avoid bug they should be recovered.
    const { testCase: testCaseData } = data;

    testCaseData.testSteps.testSteps =
      TestStepService.convertOriginObjectsToPromises(testCaseData);

    const testCase = new TestCase(
      testCaseData.id,
      testCaseData.name,
      testCaseData.testSteps,
      Promise.resolve(testCaseData.issue),
      testCaseData.expanded
    );

    this.props.syncDataFromFullScreen({ testCase });
    this.setState({ name: testCase.name });
  };

  closeTestCaseInFullScreen = (): void => {
    this.props.onDialogClose();
  };

  // eslint-disable-next-line max-lines-per-function
  render(): JSX.Element {
    return (
      <div className='test-case-box-title-wrapper'>
        <div className='test-case-box-title-icon-test-case-container'>
          <TestCaseBoxTestType
            testCaseId={this.state.issueId}
            canEdit={this.props.canEdit}
          />
        </div>

        <div className='test-case-box-title-key-test-case-wrapper'>
          <span
            className='test-case-box-title-key'
            onClick={async () =>
              window.open(
                await StorageOfUniqueServices.get('URLService').getIssueURL(
                  this.state.testCaseIssueKey
                ),
                '_parent'
              )
            }
          >
            {this.state.testCaseIssueKey}
          </span>
        </div>
        <div className='test-case-box-title-text'>
          <EditableTitle
            value={this.state.name}
            onChange={this.handleNameChange}
            onConfirm={this.handleNameChangeConfirm}
            onCancel={this.handleNameCancel}
            canEdit={this.props.canEdit}
            isInvalid={this.state.isNameInvalid}
            innerRef={this.editableTitleRef}
            onIsEditingChange={this.onIsEditingChange}
          />
          {this.props.isFullscreenMode && (
            <div className='test-case-box-title-view-change'>
              <ChangeView
                onChange={this.props.viewProps.onViewChange}
                value={this.props.viewProps.view}
                isInDialog={this.props.isFullscreenMode}
              />
            </div>
          )}
        </div>
        {this.displayTestCaseBoxActions() && (
          <div className='test-case-box-actions-wrapper'>
            <TestCaseBoxActions
              canEdit={this.props.canEdit}
              addTestStep={this.props.addTestStep}
              onDeleteTestCase={() => this.props.deleteTestCase?.()}
              isTestStepView={this.props.isTestStepView}
              testCase={this.props.testCase}
              issueKey={this.props.issueKey}
              onDropdownChange={this.onDropdownChange}
              fullScreenProps={{
                isFullscreenMode: this.props.isFullscreenMode,
                onClose: this.closeTestCaseInFullScreen,
                onOpen: this.openTestCaseInFullScreen,
              }}
            />
            <div className='test-case-box-full-screen-action'>
              <FullscreenButton
                isFullscreenMode={this.props.isFullscreenMode}
                onOpen={this.openTestCaseInFullScreen}
                onClose={this.closeTestCaseInFullScreen}
              />
            </div>
          </div>
        )}
        {!this.props.isTestStepView && (
          <div
            className='test-case-box-title-icon'
            onClick={() => this.toggleVisibility()}
          >
            <Button
              iconBefore={
                this.state.expanded ? (
                  <ChevronUpIcon size='medium' label='ChevronUpIcon' />
                ) : (
                  <ChevronDownIcon size='medium' label='ChevronDownIcon' />
                )
              }
              appearance='subtle'
            />
          </div>
        )}
      </div>
    );
  }

  handleNameChange = (name: string, invalid: boolean): void => {
    this.setState({
      name: name,
      isNameInvalid: invalid,
    });
  };

  handleNameChangeConfirm = (value: string): boolean => {
    try {
      this.setState({ name: value.trim() }, async () => {
        const name = value.trim();
        if (name !== '') {
          this.props.saveTestCaseName(name);
          this.oldName = name;
          this.setState({ nameEdited: true });
        } else {
          this.setState({ name: this.oldName });
        }
      });
      return true;
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.testCaseBoxTitle.handleNameChangeConfirm.message']
      );
      return false;
    }
  };

  handleNameCancel = (): void => {
    this.setState({
      name: this.oldName,
      isNameInvalid: false,
    });
  };
}
